import React from 'react';
import { capitalizeFirst, formatBirthdate, formatDate } from './dashboardHelpers';
import { getStatusDisplay } from './statusDisplay';

export interface BaseKit {
  serial_number: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  status: string;
  has_results: boolean;
  results_id: string | null;
  customer_birthdate?: string | null;
  customer_ssn_last4?: string | null;
  sample_collection_timestamp?: string | null;
  results_timestamp?: number | null;
}

export interface TableColumn<T> {
  header: string;
  className: string;
  render: (kit: T) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
}

/**
 * Creates a Patient Name/DOB column
 */
export const createPatientNameColumn = <T extends BaseKit>(): TableColumn<T> => ({
  header: 'Patient Name/DOB',
  className: 'patient-name',
  render: (kit: T) => (
    <div className="name-container">
      <span className="full-name">
        {kit.customer_first_name && kit.customer_last_name
          ? `${capitalizeFirst(kit.customer_first_name)} ${capitalizeFirst(kit.customer_last_name)}`
          : 'N/A'}
      </span>
      <span className="email">
        {kit.customer_birthdate ? formatBirthdate(kit.customer_birthdate) : 'N/A'}
      </span>
    </div>
  ),
});

/**
 * Creates a Status column
 */
export const createStatusColumn = <T extends BaseKit>(
  customStatusMappings?: Record<string, { text: string; color: string }>
): TableColumn<T> => ({
  header: 'Status',
  className: 'status-cell',
  render: (kit: T) => {
    const statusInfo = getStatusDisplay(kit.status, customStatusMappings);
    return (
      <span className={`status-badge ${statusInfo.color}`}>{statusInfo.text}</span>
    );
  },
});

/**
 * Creates a Results column
 */
export const createResultsColumn = <T extends BaseKit>(): TableColumn<T> => ({
  header: 'Results',
  className: 'results-cell',
  render: (kit: T) =>
    kit.has_results ? (
      <span className="results-available">Available</span>
    ) : (
      <span className="results-unavailable">Not Available</span>
    ),
});

/**
 * Creates a Collection Date column
 */
export const createCollectionDateColumn = <T extends BaseKit>(): TableColumn<T> => ({
  header: 'Collection Date',
  className: 'date-cell',
  render: (kit: T) =>
    kit.sample_collection_timestamp ? formatDate(kit.sample_collection_timestamp) : 'N/A',
});

/**
 * Creates a Results Date column
 */
export const createResultsDateColumn = <T extends BaseKit>(
  useDateTime: boolean = false
): TableColumn<T> => ({
  header: 'Results Date',
  className: 'date-cell',
  sortable: true,
  sortKey: 'results_timestamp',
  render: (kit: T) => {
    if (!kit.results_timestamp) return 'N/A';
    // return useDateTime
    //   ? formatDateTime(kit.results_timestamp)
    //   : formatDate(kit.results_timestamp);
    return formatDate(kit.results_timestamp);
  },
});

/**
 * Creates an Order Date column
 */
export const createOrderDateColumn = <T extends BaseKit>(
  useDateTime: boolean = false
): TableColumn<T> => ({
  header: 'Order Date',
  className: 'date-cell',
  sortable: true,
  sortKey: 'order_date',
  render: (kit: T) => {
    const orderDate = (kit as any).order_date;
    if (!orderDate) return 'N/A';
    // return useDateTime
    //   ? formatDateTime(orderDate)
    //   : formatDate(orderDate);
    return formatDate(orderDate);
  },
});

/**
 * Creates a Ship Date column
 */
export const createShipDateColumn = <T extends BaseKit>(
  useDateTime: boolean = false
): TableColumn<T> => ({
  header: 'Ship Date',
  className: 'date-cell',
  sortable: true,
  sortKey: 'ship_date',
  render: (kit: T) => {
    const shipDate = (kit as any).ship_date;
    if (!shipDate) return 'N/A';
    // return useDateTime
    //   ? formatDateTime(shipDate)
    //   : formatDate(shipDate);
    return formatDate(shipDate);
  },
});

/**
 * Creates a Tracking Number column
 */
export const createTrackingNumberColumn = <T extends BaseKit>(): TableColumn<T> => ({
  header: 'Tracking Number',
  className: 'tracking-cell',
  render: (kit: T) => {
    const trackingNumber = (kit as any).tracking_number;
    return trackingNumber ? (
      <span className="tracking-number">{trackingNumber}</span>
    ) : (
      <span className="no-tracking">N/A</span>
    );
  },
});

